
import { Vue, Options } from "vue-class-component";
import OverlayPanel from "primevue/overlaypanel";
import { ellipsisString, formatByCountry } from "@/app/infrastructures/misc/Utils";
import router from "@/app/ui/router";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import { NGenController } from "@/app/ui/controllers/NGenController";
import { CargoV2Data } from "@/domain/entities/CargoV2";
import { CargoV2ListRequest, DownloadAWBRequest } from "@/data/payload/api/CargoV2ApiRequest";
import { Pagination } from "@/domain/entities/Pagination";
import { FlagsPermissionCargo } from "@/feature-flags/flags-permission-cargo";
import PrintStatusIcon from "./components/print-status-icon.vue";
import CargoV2FilterSection from "./components/cargo-v2-filter-section.vue";
import CargoV2Dispute from "./components/cargo-v2-dispute.vue";
import WidgetDownloadCargo from "@/app/ui/views/cargo/component/widget-download-cargo.vue";
import ModalDownloadListCargoV2 from "./components/modal-download-list-cargo-v2.vue";
import Print from "@/app/ui/views/cargo-v2/modules/print.vue";
import { AccountController } from "../../controllers/AccountController";
import CalculateTimeDifference from "@/app/infrastructures/misc/common-library/CalculateTimeDifference";
import { FlagsPermissionCargoV2 } from "@/feature-flags/flags-permission-cargo-v2";

interface PrintStatusItem {
  label: string;
  printed: boolean;
}

type CargoV2PrintFlag = "manifest" | "label" | "pti" | "sptj";

interface CargoV2ActionGroupOption {
  icon: string;
  label: string;
  clickFunction: (item: CargoV2Data) => void;
}

interface CargoV2PrintRef {
  printManifestV2?: (
    id: string | number,
    row?: CargoV2Data | null,
    onSuccess?: (flag: CargoV2PrintFlag) => void
  ) => void;
  printData?: (
    cargoNumber: string | number,
    type: string,
    name: string,
    row?: CargoV2Data | null,
    onSuccess?: (flag: CargoV2PrintFlag) => void
  ) => void;
  printPtiPdf?: (
    id: string | number,
    row?: CargoV2Data | null,
    onSuccess?: (flag: CargoV2PrintFlag) => void
  ) => void;
  printSPTJElektronik?: (
    cargoNumber: string | number,
    row?: CargoV2Data | null,
    onSuccess?: (flag: CargoV2PrintFlag) => void
  ) => void;
  printSptjNonElectronic?: (
    id: string | number,
    row?: CargoV2Data | null,
    onSuccess?: (flag: CargoV2PrintFlag) => void
  ) => void;
}

@Options({
  components: {
    OverlayPanel,
    PrintStatusIcon,
    CargoV2FilterSection,
    CargoV2Dispute,
    WidgetDownloadCargo,
    ModalDownloadListCargoV2,
    Print
  }
})
class CargoV2BookingSuccess extends Vue {
  printPopoverItem: CargoV2Data | null = null;
  ellipsisString = ellipsisString;
  openDownloadListCargo = false;

  localFilter = new CargoV2ListRequest();

  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  get cargoSuccessSummary() {
    const s = CargoV2Controller.cargoSuccessSummaryData;
    return { plane: s?.plane ?? 0, truck: s?.truck ?? 0, ship: s?.ship ?? 0 };
  }

  get cargoList(): CargoV2Data[] {
    return CargoV2Controller.cargoV2List.data;
  }

  get pagination() {
    return CargoV2Controller.pagination;
  }

  set pagination(p: typeof CargoV2Controller.pagination) {
    CargoV2Controller.setPagination(p);
  }

  get isLoading(): boolean {
    return CargoV2Controller.isLoading;
  }

  get errorCause(): string {
    return CargoV2Controller.errorCause;
  }

  get cargoListAvailable(): boolean {
    return (
      !this.cargoList.length && !this.isLoading && this.pagination.page === 1
    );
  }

  get columns() {
    return [
      {
        name: this.$t("cargo.labels.cargoNumberSmu"),
        styleHead: "w-80 text-left whitespace-no-wrap align-middle break-all",
        "custom-slot": true,
        slotName: "number_cargo"
      },
      {
        name: this.$t("cargo.labels.originDestinationCities"),
        styleHead:
          "min-w-[12rem] w-40 text-left whitespace-nowrap align-middle",
        "custom-slot": true,
        slotName: "origin_destination"
      },
      {
        name: this.$t("cargo.v2.column.etd"),
        styleHead: "w-40 text-left",
        "custom-slot": true,
        slotName: "etd"
      },
      {
        name: this.$t("cargo.v2.column.totalPiecesGrossWeightShort"),
        styleHead: "w-56 text-left whitespace-nowrap align-middle",
        render: (item: CargoV2Data) => {
          const translatedText = this.$t(
            "cargo.v2.column.piecesGrossWeightValue",
            {
              pieces: item.cargoActualPiece,
              weight: formatByCountry(item.cargoTotalCustomGrossWeight, { type: "weight", country: this.countryCode })
            }
          );

          return `<div class="overflow-ellipsis text-left">${translatedText}</div>`;
        }
      },
      {
        name: this.$t("cargo.v2.column.printStatus"),
        styleHead: "w-40 text-left whitespace-no-wrap align-middle",
        "custom-slot": true,
        slotName: "status_print"
      },
      {
        name: this.$t("cargo.labels.weightDifference"),
        styleHead: "w-48 text-left align-middle",
        "custom-slot": true,
        slotName: "selisih_berat"
      },
      {
        name: this.$t("cargo.labels.lastUpdated"),
        styleHead: "w-56 text-left whitespace-no-wrap align-middle",
        render: (item: CargoV2Data) => {
          const date = item.updatedAt
            ? this.formatDate(item.updatedAt, "DD MMMM YYYY, HH:mm")
            : item.createdAt
            ? this.formatDate(item.createdAt, "DD MMMM YYYY, HH:mm")
            : "-";
          const translateText = this.$t("cargo.v2.datetime.timezoneSuffix", {
            date
          });
          return item.updatedAt || item.createdAt
            ? `<div class="flex flex-col text-sm text-gray-lp-300">
                  <div class="overflow-ellipsis text-left"> ${translateText} </div>
                  <div class="truncate text-gray-lp-600 w-32">
                    ${item.updatedBy || item.createdBy || "-"}
                  </div>
                  <div> ${item.cargoHubName || "-"} </div>
                </div>`
            : `<div class="overflow-ellipsis text-left"> - </div>`;
        }
      },
      {
        name: this.$t("cargo.labels.action"),
        key: "actionable_column",
        styleHead: "w-28 text-left align-middle",
        showButton: () => true
      }
    ];
  }

  get selectedCargoTypes(): string[] {
    return this.localFilter.cargoTypes
      ? this.localFilter.cargoTypes.split(",")
      : [];
  }

  setPrintFlag(row: CargoV2Data, flag: CargoV2PrintFlag): void {
    if (flag === "manifest") row.isPrintManifest = true;
    else if (flag === "label") row.isPrintLabel = true;
    else if (flag === "pti") row.isPrintPti = true;
    else if (flag === "sptj") row.isPrintSptj = true;
  }

  getPrintStatusItems(item: CargoV2Data): PrintStatusItem[] {
    const isPlane = item.cargoType === "plane";
    const isBTH =
      item.originCityCode === "BTH" || item.destinationCityCode === "BTH";

    const label = {
      label: this.$t("cargo.v2.print.labelCargo"),
      printed: item.isPrintLabel
    };
    const awb = {
      label: this.$t("cargo.v2.print.awbLionCargo"),
      printed: item.isPrintAwb
    };
    const pti = {
      label: this.$t("cargo.v2.print.pti"),
      printed: item.isPrintPti
    };
    const sptj = {
      label: this.$t("cargo.v2.print.sptj"),
      printed: item.isPrintSptj
    };
    const manifest = {
      label: this.$t("cargo.v2.print.manifestCargo"),
      printed: item.isPrintManifest
    };

    if (!isPlane) return [label, manifest];

    if (isBTH) return [label, awb, manifest];

    const items: PrintStatusItem[] = [label, awb];

    if (FlagsPermissionCargo.permission_cargo_print_pti_pdf.isEnabled()) {
      items.push(pti);
    }

    if (
      (FlagsPermissionCargo.permission_cargo_print_electronic_sptj_pdf.isEnabled() &&
        item.cargoCommodityGroupIsElectronicDg) ||
      (FlagsPermissionCargo.permission_cargo_print_sptj_pdf.isEnabled() &&
        item.cargoCommodityGroupIsDg &&
        !item.cargoCommodityGroupIsElectronicDg)
    ) {
      items.push(sptj);
    }

    items.push(manifest);

    return items;
  }

  actionGroupOptions(item: CargoV2Data): CargoV2ActionGroupOption[] {
    const onPrintSuccess = (row: CargoV2Data) => (flag: CargoV2PrintFlag) =>
      this.setPrintFlag(row, flag);

    const print = this.$refs.print as CargoV2PrintRef;

    const labelOption: CargoV2ActionGroupOption = {
      icon: "invoice",
      label: this.$t("cargo.v2.actions.printLabel"),
      clickFunction: (row) => {
        print?.printData?.(
          row.id,
          "label",
          "",
          row,
          onPrintSuccess(row)
        );
      }
    };

    const manifestOption: CargoV2ActionGroupOption = {
      icon: "printer",
      label: this.$t("cargo.v2.actions.printManifest"),
      clickFunction: (row) => {
        print?.printManifestV2?.(row.id, row, onPrintSuccess(row));
      }
    };

    const excelOption: CargoV2ActionGroupOption = {
      icon: "document-download-outline-grey",
      label: this.$t("cargo.actions.downloadExcel"),
      clickFunction: (row) => {
        const filename = `Manifest cargo ${row.cargoNumber} - ${row.destinationCityCode}`
        CargoV2Controller.getDownloadData({ id: row.id, type: "cargo", filename });
      }
    };

    const permissionDownloadAwbReceiptSync = FlagsPermissionCargoV2.permission_cargo_v2_download_awb_receipt_sync_enable.isEnabled();
    const awbOption: CargoV2ActionGroupOption = {
      icon: "printer",
      label: this.$t("cargo.v2.actions.printAwb"),
      clickFunction: (row) => {
        if (row.isNewForm) {
          CargoV2Controller.downloadAwbReceipt(new DownloadAWBRequest({id: row.id, awbNumber: row.cargoNumber}));
          if (permissionDownloadAwbReceiptSync) {
            row.isPrintAwb = true;
          }
        } else {
          NGenController.printAWBLionCargo({ AWBNo: row.cargoNumber });
        }
      }
    };

    const eplOption: CargoV2ActionGroupOption = {
      icon: "download-gray",
      label: this.$t("cargo.actions.downloadEpl"),
      clickFunction: (row) => {
        CargoV2Controller.getDownloadData({ id: row.id, type: "epl", filename: "" });
      }
    };

    const ptiOption: CargoV2ActionGroupOption = {
      icon: "print-pti",
      label: this.$t("cargo.v2.actions.printPti"),
      clickFunction: (row) => {
        print?.printPtiPdf?.(row.id, row, onPrintSuccess(row));
      }
    };

    const sptjElectronicOption: CargoV2ActionGroupOption = {
      icon: "print-sptj",
      label: this.$t("cargo.v2.actions.printSptj"),
      clickFunction: (row) => {
        print?.printSPTJElektronik?.(row.id, row, onPrintSuccess(row));
      }
    };

    const sptjNonElectronicOption: CargoV2ActionGroupOption = {
      icon: "print-sptj",
      label: this.$t("cargo.v2.actions.printSptj"),
      clickFunction: (row) => {
        print?.printSptjNonElectronic?.(
          row.id,
          row,
          onPrintSuccess(row)
        );
      }
    };

    const isPlane = item.cargoType === "plane";
    const isBTH =
      item.originCityCode === "BTH" || item.destinationCityCode === "BTH";

    // ===============================
    // 1️⃣ Non plane
    // ===============================
    if (!isPlane) {
      return [labelOption, manifestOption, excelOption];
    }

    // ===============================
    // 2️⃣ Plane + BTH
    // ===============================
    if (isPlane && isBTH) {
      return [labelOption, awbOption, eplOption, manifestOption, excelOption];
    }

    // ===============================
    // 3️⃣ Plane Normal
    // ===============================
    const options: CargoV2ActionGroupOption[] = [labelOption, awbOption];

    if (FlagsPermissionCargo.permission_cargo_print_pti_pdf.isEnabled()) {
      options.push(ptiOption);
    }

    if (
      FlagsPermissionCargo.permission_cargo_print_electronic_sptj_pdf.isEnabled() &&
      item.cargoCommodityGroupIsElectronicDg
    ) {
      options.push(sptjElectronicOption);
    }

    if (
      FlagsPermissionCargo.permission_cargo_print_sptj_pdf.isEnabled() &&
      item.cargoCommodityGroupIsDg &&
      !item.cargoCommodityGroupIsElectronicDg
    ) {
      options.push(sptjNonElectronicOption);
    }

    options.push(manifestOption);
    options.push(excelOption);

    return options;
  }

  onSuccessPrintAwb(cargoNo: string): void {
    const cargo = this.cargoList.find((item) => item.cargoNumber === cargoNo);
    if (cargo) {
      cargo.isPrintAwb = true;
    }
  }

  formatDate(dateStr: string, format = "DD MMMM YYYY"): string {
    if (!dateStr) return "";
    return this.$moment(dateStr).format(format);
  }

  etdDayOffset(item: CargoV2Data): number {
    if (!item.estimationDepartureDate || !item.estimationArrivalDate) return 0;
    const dep = this.$moment(item.estimationDepartureDate).startOf("day");
    const arr = this.$moment(item.estimationArrivalDate).startOf("day");
    return Math.max(0, arr.diff(dep, "days"));
  }

  etdDayOffsetLabel(item: CargoV2Data): string {
    const days = this.etdDayOffset(item);
    if (days === 0) return "";
    return days === 1
      ? this.$t("cargo.v2.etd.dayOffsetSingular")
      : this.$t("cargo.v2.etd.dayOffsetPlural", { count: days });
  }

  fetchInterval: ReturnType<typeof setInterval> | null = null;

  startInterval(): void {
    this.fetchInterval = setInterval(async () => {
      await this.fetchLastIdCargo();
    }, FlagsPermissionCargo.config_interval_auto_refresh_cargo.getValue() * 1000);
  }

  stopInterval(): void {
    if (this.fetchInterval) {
      clearInterval(this.fetchInterval);
      this.fetchInterval = null;
    }
  }

  mounted(): void {
    this.fetchLastIdCargo();
    this.startInterval();
  }

  unmounted(): void {
    this.stopInterval();
  }

  onSearch(value: string) {
    this.localFilter.search = value;

    const p = CargoV2Controller.pagination;
    CargoV2Controller.setPagination(new Pagination(1, p?.limit, p?.totalData));

    this.fetchList();
  }

  onFilterChange(payload: CargoV2ListRequest): void {
    this.localFilter = payload;
    const p = CargoV2Controller.pagination;
    CargoV2Controller.setPagination(new Pagination(1, p?.limit, p?.totalData));

    this.fetchList();

    CargoV2Controller.getCargoSuccessSummary(
      new CargoV2ListRequest({
        ...this.localFilter,
        search: "",
        cargoType: "",
        limit: 10,
        isTotalData: true
      })
    );
  }

  async fetchList(): Promise<void> {
    await CargoV2Controller.fetchList(
      new CargoV2ListRequest({
        ...this.localFilter,
        originCity: this.originCity,
        page: CargoV2Controller.pagination?.page,
        limit: CargoV2Controller.pagination?.limit
      })
    );
  }

  get originCity() {
    return (
      this.localFilter.originCity ||
      AccountController.accountData.account_location.city_code
    );
  }

  lastIdCargo = 0;
  async fetchLastIdCargo() {
    const newLastIdCargo = await CargoV2Controller.fetchLastIdCargoRetryCargo(
      new CargoV2ListRequest({ type: "cargo" })
    );
    if (this.lastIdCargo === 0 && newLastIdCargo !== 0) {
      this.lastIdCargo = newLastIdCargo;
    }
    if (this.lastIdCargo === 0 || newLastIdCargo === 0) return;
    if (this.lastIdCargo !== newLastIdCargo) {
      this.lastIdCargo = newLastIdCargo;
      await this.fetchList();
    }
  }

  onClickRow(item: CargoV2Data): void {
    if (item.isNewForm) {
      router.push(`/cargo-v2/${encodeURIComponent(item.cargoNumber)}/info?id=${item.id}`);
      return;
    }

    router.push(`/cargo/${encodeURIComponent(item.cargoNumber)}?id=${item.id}`);
  }

  openPrintPopover(event: Event, item: CargoV2Data): void {
    this.printPopoverItem = item;
    const op = this.$refs.printPopover as { toggle?: (e: Event) => void };
    op?.toggle?.(event);
  }

  closePrintPopover(): void {
    const op = this.$refs.printPopover as { hide?: () => void };
    op?.hide?.();
  }

  CalculateTimeDifference = CalculateTimeDifference;
  showRemainingTimePlane(
    time: string,
    cargoType: string,
    departureDate: string
  ) {
    return !(!time || cargoType !== "plane" || departureDate);
  }
  get DateNow() {
    return this.$moment().format("YYYY-MM-DDTHH:mm:ss");
  }
}

export default CargoV2BookingSuccess;
